import React, { useEffect, useState } from "react";
import { Plus, Trash2, KeyRound, Save, X, MapPin, Pencil, Home, Building2 } from "lucide-react";
import http from "@/lib/http";
import { t } from "@/i18n";
import { useAuth } from "@/lib/auth";

export default function Users() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "super_admin" || me?.role === "tenant_admin";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editingPostcodes, setEditingPostcodes] = useState(null); // user_id
  const [pcMakInput, setPcMakInput] = useState("");
  const [pcParInput, setPcParInput] = useState("");
  const [resetPwFor, setResetPwFor] = useState(null);
  const [pwInput, setPwInput] = useState("");
  const [savedFlash, setSavedFlash] = useState(null);
  const [error, setError] = useState(null);

  const refresh = async () => {
    setLoading(true);
    const r = await http.get("/api/users");
    setUsers(r.data);
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  if (!isAdmin) {
    return (
      <div className="flex-1 p-8" data-testid="users-screen-forbidden">
        <h1 className="font-display text-[28px]">{t("users.title")}</h1>
        <p className="text-[var(--ink-500)] mt-2">Alleen toegankelijk voor admins.</p>
      </div>
    );
  }

  const startEditPostcodes = (u) => {
    setEditingPostcodes(u.id);
    setPcMakInput((u.postcodes_makelaar || u.postcodes || []).join(", "));
    setPcParInput((u.postcodes_particulier || u.postcodes || []).join(", "));
    setError(null);
  };
  const savePostcodes = async (uid) => {
    const parse = (s) => s.split(",").map(x => x.trim()).filter(Boolean);
    await http.put(`/api/users/${uid}/postcodes`, {
      postcodes_makelaar: parse(pcMakInput),
      postcodes_particulier: parse(pcParInput),
    });
    setEditingPostcodes(null);
    setSavedFlash(uid);
    setTimeout(() => setSavedFlash(null), 1500);
    refresh();
  };
  const submitResetPw = async (uid) => {
    if (pwInput.length < 6) { setError("Minimaal 6 tekens"); return; }
    try {
      await http.put(`/api/users/${uid}/password`, { password: pwInput });
      setResetPwFor(null);
      setPwInput("");
      setError(null);
      setSavedFlash(uid);
      setTimeout(() => setSavedFlash(null), 1500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Fout bij opslaan");
    }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(t("users.delete_confirm", { name: u.name }))) return;
    try {
      await http.delete(`/api/users/${u.id}`);
      refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Fout");
    }
  };

  return (
    <div className="flex-1 p-8 overflow-auto" data-testid="users-screen">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-[13px] text-[var(--teal-700)] font-medium mb-2 uppercase tracking-[0.14em]">
            {t("nav.settings")}
          </div>
          <h1 className="font-display text-[32px] leading-tight text-[var(--ink-900)]">
            {t("users.title")}
          </h1>
          <p className="text-[14px] text-[var(--ink-500)] mt-1 max-w-2xl">{t("users.sub")}</p>
        </div>
        <button type="button" data-testid="btn-new-user" className="btn btn-primary"
                onClick={() => setShowNew(true)}>
          <Plus size={16} /> {t("users.new")}
        </button>
      </div>

      {loading ? (
        <div className="text-[var(--ink-500)] text-sm">{t("common.loading")}</div>
      ) : users.length === 0 ? (
        <div className="card text-center py-12 px-6">
          <h3 className="font-display text-[20px]">{t("users.empty_title")}</h3>
          <p className="text-[14px] text-[var(--ink-500)] mt-2">{t("users.empty_body")}</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="users-list">
          {users.map(u => {
            const isEditing = editingPostcodes === u.id;
            const isResettingPw = resetPwFor === u.id;
            const initials = (u.name || "?").split(" ").map(p => p[0]).slice(0, 2).join("").toUpperCase();
            return (
              <div key={u.id} data-testid={`user-row-${u.id}`}
                   className="card !p-5">
                {/* Header row: avatar + name + email + role + actions */}
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="w-11 h-11 rounded-full bg-[var(--teal-100)] text-[var(--teal-700)] grid place-items-center font-semibold text-[15px] shrink-0">
                    {initials}
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <InlineNameEdit user={u} onSaved={refresh} />
                      {u.id === me?.id && <span className="chip !text-[10px] !py-[1px]">jij</span>}
                      <span className="chip chip-teal !text-[11px] !py-[1px]">{t(`roles.${u.role}`)}</span>
                      {savedFlash === u.id && (
                        <span className="text-[11px] text-[var(--teal-700)] flex items-center gap-1">
                          <Save size={11} /> {t("users.saved")}
                        </span>
                      )}
                    </div>
                    <div className="text-[13px] text-[var(--ink-500)] mt-0.5">{u.email}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!isEditing && (
                      <button type="button" data-testid={`btn-edit-pc-${u.id}`}
                              onClick={() => startEditPostcodes(u)}
                              title={t("users.edit_postcodes")}
                              className="btn btn-ghost !h-9 !px-3 !text-[12px]">
                        <MapPin size={13} /> Postcodes
                      </button>
                    )}
                    <button type="button" data-testid={`btn-reset-pw-${u.id}`}
                            onClick={() => { setResetPwFor(u.id); setPwInput(""); setError(null); }}
                            title={t("users.reset_password")}
                            className="p-2 rounded-md hover:bg-[var(--sand-100)] text-[var(--ink-500)]">
                      <KeyRound size={15} />
                    </button>
                    {u.id !== me?.id && (
                      <button type="button" data-testid={`btn-delete-user-${u.id}`}
                              onClick={() => deleteUser(u)}
                              title={t("users.delete")}
                              className="p-2 rounded-md hover:bg-[var(--sand-100)] text-[var(--ink-500)] hover:text-[var(--danger)]">
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Password reset row (inline) */}
                {isResettingPw && (
                  <div className="mt-3 pt-3 border-t border-[var(--border-soft)] flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] uppercase tracking-wider text-[var(--ink-500)]">Nieuw wachtwoord</span>
                    <input type="password" autoFocus
                           data-testid={`pw-input-${u.id}`}
                           value={pwInput}
                           onChange={(e) => setPwInput(e.target.value)}
                           placeholder={t("users.new_password")}
                           className="h-9 px-2 rounded-md border border-[var(--border-soft)] text-[13px] flex-1 min-w-[200px] max-w-[280px]" />
                    <button type="button" data-testid={`pw-save-${u.id}`}
                            onClick={() => submitResetPw(u.id)}
                            className="btn btn-primary !h-9 !px-3 !text-[12px]">
                      <Save size={13} /> Opslaan
                    </button>
                    <button type="button" onClick={() => setResetPwFor(null)}
                            className="btn btn-ghost !h-9 !px-2 !text-[12px]">
                      <X size={13} />
                    </button>
                    {error && <span className="text-[11px] text-[var(--danger)] w-full">{error}</span>}
                  </div>
                )}

                {/* Postcodes section */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {isEditing ? (
                    <>
                      <PostcodeEditCard
                        icon={<Building2 size={13} />}
                        label="Makelaar"
                        accent="teal"
                        value={pcMakInput}
                        onChange={setPcMakInput}
                        testid={`pc-input-makelaar-${u.id}`}
                        autoFocus
                      />
                      <PostcodeEditCard
                        icon={<Home size={13} />}
                        label="Particulier"
                        accent="sand"
                        value={pcParInput}
                        onChange={setPcParInput}
                        testid={`pc-input-particulier-${u.id}`}
                      />
                    </>
                  ) : (
                    <>
                      <PostcodeViewCard
                        icon={<Building2 size={13} />}
                        label="Makelaar"
                        accent="teal"
                        codes={u.postcodes_makelaar}
                        fallback={u.postcodes}
                        userId={u.id}
                      />
                      <PostcodeViewCard
                        icon={<Home size={13} />}
                        label="Particulier"
                        accent="sand"
                        codes={u.postcodes_particulier}
                        fallback={u.postcodes}
                        userId={u.id}
                      />
                    </>
                  )}
                </div>

                {isEditing && (
                  <div className="mt-3 flex items-center justify-end gap-2">
                    <button type="button" onClick={() => setEditingPostcodes(null)}
                            className="btn btn-ghost !h-9 !px-3 !text-[12px]">
                      <X size={13} /> Annuleren
                    </button>
                    <button data-testid={`pc-save-${u.id}`} type="button"
                            onClick={() => savePostcodes(u.id)}
                            className="btn btn-primary !h-9 !px-4 !text-[12px]">
                      <Save size={13} /> Opslaan
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showNew && <NewUserDialog onClose={() => setShowNew(false)}
                                 onCreated={() => { setShowNew(false); refresh(); }} />}
    </div>
  );
}

function PostcodeViewCard({ icon, label, accent, codes, fallback, userId }) {
  const list = (codes && codes.length) ? codes : (fallback || []);
  const isFallback = !codes || codes.length === 0;
  const [expanded, setExpanded] = useState(false);
  const COMPACT_LIMIT = 8;
  const showAll = expanded || list.length <= COMPACT_LIMIT;
  const visible = showAll ? list : list.slice(0, COMPACT_LIMIT);
  const hidden = list.length - visible.length;
  const accentBorder = accent === "teal"
    ? "border-l-[var(--teal-500)]"
    : "border-l-[#E5C9A0]";
  const headerBg = accent === "teal" ? "bg-[var(--teal-50)]" : "bg-[var(--sand-100)]";
  const headerFg = accent === "teal" ? "text-[var(--teal-800)]" : "text-[#7C5A1E]";
  return (
    <div className={`rounded-lg border border-[var(--border-soft)] border-l-4 ${accentBorder} overflow-hidden`}
         data-testid={`postcode-card-${label.toLowerCase()}-${userId}`}>
      <div className={`${headerBg} ${headerFg} px-3 py-2 flex items-center justify-between text-[12px] font-semibold uppercase tracking-wider`}>
        <span className="flex items-center gap-1.5">{icon} {label}</span>
        <span className="text-[11px] tabular">
          {list.length} {list.length === 1 ? "postcode" : "postcodes"}
        </span>
      </div>
      <div className="px-3 py-2.5 bg-white min-h-[42px]">
        {list.length === 0 ? (
          <span className="text-[var(--ink-400)] text-[12px] italic">geen postcodes</span>
        ) : (
          <div className="flex flex-wrap gap-1.5 items-center">
            {visible.map(pc => (
              <span key={pc} className={`chip !text-[11px] !py-[2px] ${
                isFallback ? "bg-[var(--sand-100)] text-[var(--ink-700)] border-[var(--border-soft)]" : "chip-teal"
              }`}>{pc}</span>
            ))}
            {hidden > 0 && (
              <button type="button" onClick={() => setExpanded(true)}
                      data-testid={`postcode-expand-${label.toLowerCase()}-${userId}`}
                      className="text-[11px] text-[var(--teal-700)] font-medium hover:underline px-1">
                +{hidden} meer
              </button>
            )}
            {expanded && list.length > COMPACT_LIMIT && (
              <button type="button" onClick={() => setExpanded(false)}
                      className="text-[11px] text-[var(--ink-500)] hover:underline px-1">
                Inklappen
              </button>
            )}
            {isFallback && list.length > 0 && (
              <span className="text-[10px] text-[var(--ink-400)] italic ml-1"
                    title="Geen aparte lijst ingesteld — overgenomen van de algemene postcode-lijst">
                · uit fallback
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PostcodeEditCard({ icon, label, accent, value, onChange, testid, autoFocus }) {
  const accentBorder = accent === "teal"
    ? "border-l-[var(--teal-500)]"
    : "border-l-[#E5C9A0]";
  const headerBg = accent === "teal" ? "bg-[var(--teal-50)]" : "bg-[var(--sand-100)]";
  const headerFg = accent === "teal" ? "text-[var(--teal-800)]" : "text-[#7C5A1E]";
  const codes = value.split(",").map(s => s.trim()).filter(Boolean);
  return (
    <div className={`rounded-lg border border-[var(--border-soft)] border-l-4 ${accentBorder} overflow-hidden`}>
      <div className={`${headerBg} ${headerFg} px-3 py-2 flex items-center justify-between text-[12px] font-semibold uppercase tracking-wider`}>
        <span className="flex items-center gap-1.5">{icon} {label}</span>
        <span className="text-[11px] tabular">{codes.length}</span>
      </div>
      <div className="px-3 py-2.5 bg-white space-y-1.5">
        <input
          autoFocus={autoFocus}
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="bv. 2830, 2800, 2845"
          className="w-full h-9 px-2 rounded-md border border-[var(--border-soft)] text-[13px] focus:border-[var(--teal-500)] outline-none"
        />
        <div className="text-[10px] text-[var(--ink-500)]">
          Gescheiden door komma's. Leeg = gebruikt fallback (algemene lijst).
        </div>
      </div>
    </div>
  );
}

function NewUserDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "sales_agent",
    postcodes_makelaar: "", postcodes_particulier: "",
  });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const parse = (s) => s.split(",").map(x => x.trim()).filter(Boolean);
      const pc_mak = parse(form.postcodes_makelaar);
      const pc_par = parse(form.postcodes_particulier);
      await http.post("/api/users", {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        name: form.name.trim(),
        role: form.role,
        postcodes_makelaar: pc_mak,
        postcodes_particulier: pc_par,
      });
      onCreated();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Aanmaken mislukt");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/30 z-40 grid place-items-center p-4" data-testid="new-user-dialog">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div>
          <h2 className="font-display text-[22px]">{t("users.new")}</h2>
        </div>
        <div className="space-y-3">
          <Field label={t("users.name")}>
            <input required data-testid="nu-name" value={form.name}
                   onChange={(e) => setForm({...form, name: e.target.value})}
                   className="w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] text-sm" />
          </Field>
          <Field label={t("users.email")}>
            <input required type="email" data-testid="nu-email" value={form.email}
                   onChange={(e) => setForm({...form, email: e.target.value})}
                   className="w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] text-sm" />
          </Field>
          <Field label={t("users.password")}>
            <input required type="password" data-testid="nu-password" value={form.password}
                   onChange={(e) => setForm({...form, password: e.target.value})}
                   minLength={6}
                   className="w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] text-sm" />
          </Field>
          <Field label={t("users.role")}>
            <select data-testid="nu-role" value={form.role}
                    onChange={(e) => setForm({...form, role: e.target.value})}
                    className="w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] text-sm">
              <option value="sales_agent">{t("roles.sales_agent")}</option>
              <option value="tenant_admin">{t("roles.tenant_admin")}</option>
              <option value="super_admin">{t("roles.super_admin")}</option>
            </select>
          </Field>
          <Field label="Postcodes Makelaar">
            <input data-testid="nu-postcodes-makelaar" value={form.postcodes_makelaar}
                   onChange={(e) => setForm({...form, postcodes_makelaar: e.target.value})}
                   placeholder="bv. 2830, 2800, 2845"
                   className="w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] text-sm" />
          </Field>
          <Field label="Postcodes Particulier">
            <input data-testid="nu-postcodes-particulier" value={form.postcodes_particulier}
                   onChange={(e) => setForm({...form, postcodes_particulier: e.target.value})}
                   placeholder="bv. 2830, 2845"
                   className="w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] text-sm" />
          </Field>
        </div>
        {err && <div className="text-[12px] text-[var(--danger)]">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn btn-ghost">{t("users.cancel")}</button>
          <button type="submit" data-testid="btn-create-user" disabled={busy} className="btn btn-primary">
            {busy ? "…" : t("users.create")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[12px] font-medium text-[var(--ink-700)] mb-1">{label}</div>
      {children}
    </label>
  );
}

function InlineNameEdit({ user, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(user.name);
  useEffect(() => { setVal(user.name); }, [user.name]);
  const save = async () => {
    const v = val.trim();
    if (!v || v === user.name) { setEditing(false); return; }
    await http.put(`/api/users/${user.id}/name`, { name: v });
    setEditing(false);
    onSaved();
  };
  if (!editing) {
    return (
      <span className="group inline-flex items-center gap-1.5">
        <span data-testid={`user-name-${user.id}`}>{user.name}</span>
        <button type="button" onClick={() => setEditing(true)}
                data-testid={`user-name-edit-${user.id}`}
                className="opacity-0 group-hover:opacity-100 p-1 rounded text-[var(--ink-400)] hover:bg-[var(--sand-100)] hover:text-[var(--ink-700)] transition-opacity">
          <Pencil size={11} />
        </button>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <input autoFocus value={val}
             data-testid={`user-name-input-${user.id}`}
             onChange={(e) => setVal(e.target.value)}
             onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setVal(user.name); setEditing(false); } }}
             className="h-8 px-2 rounded-md border border-[var(--teal-500)] text-[13px]" />
      <button type="button" onClick={save}
              data-testid={`user-name-save-${user.id}`}
              className="p-1 text-[var(--teal-700)] hover:bg-[var(--sand-100)] rounded">
        <Save size={12} />
      </button>
    </span>
  );
}
